import React, { useContext, useEffect, useState } from "react";
import { Breadcrumb, Button, Form, Modal, OverlayTrigger, Table } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { BiDownArrow, BiDownArrowAlt, BiPencil, BiPlus, BiPlusCircle, BiTransfer, BiTrash, BiUpArrow, BiUpArrowAlt } from "react-icons/bi";
import './Questions.scss';
import QuestionService from "../../../core/service/QuestionService";
import { FcFinePrint } from "react-icons/fc";
import { QuestionType, QuestionTypeLabel, Pages, VSME_PAGES, QUESTION_STANDARDS, GHG_PAGES, DataPeriod, UAE_FDL_PAGES } from "../../../core/constants/common";
import Utils from "../../../core/utilities/Utils";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import { LoaderContext } from "../../../App";

const Questions = ({ t }) => {

	const [standards, setStandards] = useState([]);
	const [standardToMap, setStandardToMap] = useState('ESRS');
	const [questions, setQuestions] = useState([]);
	const [showAddQuestion, setShowAddQuestion] = useState(false);
	const [saveChanges, setSaveChanges] = useState(false);
	const [draggingItemIndex, setDraggingItemIndex] = useState(null);
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
	const [selectedForDelete, setSelectedForDelete] = useState({});
	const [page, setPage] = useState('1');
	const [newData, setNewData] = useState({
		key: "",
		frameworkCode: "",
		groupId: "",
		page: '1',
		keyValueType: "PAGE",
		sortOrder: 0,
		// keyValues: [],
		localization: [{
			languageCode: "EN",
			text: "",
			type: "REPORTING_STUDIO",
		}]
	});

	const [keyValues, setKeyValues] = useState([
		{
			key: "",
			dataPeriod: DataPeriod.NONE,
			metadata: {
				columnPosition: "TOP",
				columnOrder: 1,
				columnNumber: "",
				columnType: "HEADER",
				localization: [{
					languageCode: "EN",
					type: "REPORTING_METADATA",
					text: "",
				}],
			},
			// platformCode: [{
			// 	code: "ECD001",
			// 	domain: "QUALITATIVE_METRIC"
			// }]
		},
	]);
	const languageOptions = [
		{ value: 'EN', label: 'English' },
		{ value: 'AR', label: 'Arabic' },
		{ value: 'DE', label: 'German' },
	];
	const { setLoaderDisplay } = useContext(LoaderContext);

	// Default keyValue row for table types (TYPE_1–7) – same structure as TYPE_5 so keyValues is never empty when in table type
	const getDefaultKeyValueRow = () => ({
		key: "",
		dataPeriod: DataPeriod.NONE,
		metadata: {
			columnPosition: "TOP",
			columnOrder: 1,
			columnNumber: "",
			columnType: "HEADER",
			localization: [{ languageCode: "EN", type: "REPORTING_METADATA", text: "" }],
		},
	});

	const isTableTypeKey = (keyValueType) =>
		['TYPE_1_TABLE', 'TYPE_2_TABLE', 'TYPE_3_TABLE', 'TYPE_5_TABLE', 'TYPE_6_TABLE', 'TYPE_7_TABLE'].includes(keyValueType);

	useEffect(() => {
		getQuestionsData();
	}, [standardToMap, page]);

	const swapItems = (array, index1, index2) => {
		const newArray = [...array];
		[newArray[index1], newArray[index2]] = [newArray[index2], newArray[index1]];
		return newArray;
	};

	const recalculateKeyValueOrder = (array) => {
		return array.map((item, index) => ({
			...item,
			metadata: {
				...item.metadata,
				columnOrder: index + 1,
			},
		}));
	};

	const recalculateLocalizationOrder = (localizations) => {
		return localizations.map((loc, index) => ({
			...loc,
			columnOrder: index + 1, // 1-based index
		}));
	};

	const moveKeyValue = (index, direction) => {
		if (
			(direction === "up" && index > 0) ||
			(direction === "down" && index < keyValues.length - 1)
		) {
			const newOrder = swapItems(keyValues, index, direction === "up" ? index - 1 : index + 1);
			setKeyValues(recalculateKeyValueOrder(newOrder));
		}
	};

	const moveLocalization = (keyIndex, locIndex, direction) => {
		const localizations = keyValues[keyIndex].metadata.localization;
		if (
			(direction === "up" && locIndex > 0) ||
			(direction === "down" && locIndex < localizations.length - 1)
		) {
			const updatedLocalizations = swapItems(
				localizations,
				locIndex,
				direction === "up" ? locIndex - 1 : locIndex + 1
			);
			const updatedKeyValues = [...keyValues];
			updatedKeyValues[keyIndex].metadata.localization = updatedLocalizations;
			setKeyValues(updatedKeyValues);
		}
	};

	const addKeyValue = () => {
		const newKeyValue = {
			...getDefaultKeyValueRow(),
			metadata: {
				...getDefaultKeyValueRow().metadata,
				columnOrder: keyValues.length + 1,
			},
		};
		setKeyValues(recalculateKeyValueOrder([...keyValues, newKeyValue]));
	};

	const deleteKeyValue = (index) => {
		const updatedKeyValues = keyValues.filter((_, i) => i !== index);
		setKeyValues(recalculateKeyValueOrder(updatedKeyValues));
	};

	const addLocalization = (index) => {
		if (keyValues[index].metadata.localization.length < 3) {
			const updatedKeyValues = [...keyValues];
			updatedKeyValues[index].metadata.localization.push({
				languageCode: "",
				type: "REPORTING_METADATA",
				text: "",
			});
			updatedKeyValues[index].metadata.localization = recalculateLocalizationOrder(
				updatedKeyValues[index].metadata.localization
			);
			setKeyValues(updatedKeyValues);
		}
	};

	const deleteLocalization = (keyIndex, locIndex) => {
		const updatedKeyValues = [...keyValues];
		updatedKeyValues[keyIndex].metadata.localization = updatedKeyValues[
			keyIndex
		].metadata.localization.filter((_, i) => i !== locIndex);
		updatedKeyValues[keyIndex].metadata.localization = recalculateLocalizationOrder(
			updatedKeyValues[keyIndex].metadata.localization
		);
		setKeyValues(updatedKeyValues);
	};

	const updateKeyValue = (index, field, value) => {
		const updatedKeyValues = [...keyValues];
		updatedKeyValues[index][field] = value;
		setKeyValues(updatedKeyValues);
	};

	const updateDataPeriod = (index, checked) => {
		const updatedKeyValues = [...keyValues];
		updatedKeyValues[index].dataPeriod = checked ? "BASE_YEAR" : "NONE";
		setKeyValues(updatedKeyValues);
	};

	const updatePositionValue = (index, field, value) => {
		const updatedKeyValues = [...keyValues];
		updatedKeyValues[index].metadata[field] = value;
		// Reset dataPeriod to NONE if columnType changes away from CODE_MAPPING
		if (field === 'columnType' && value !== 'CODE_MAPPING') {
			updatedKeyValues[index].dataPeriod = DataPeriod.NONE;
		}
		setKeyValues(updatedKeyValues);
	};

	const updateLocalization = (keyIndex, locIndex, field, value) => {
		const updatedKeyValues = [...keyValues];
		updatedKeyValues[keyIndex].metadata.localization[locIndex][field] = value;
		setKeyValues(updatedKeyValues);
	};

	const getStandards = () => {
		QuestionService.getPlatformStandard().then(res => {
			if (res.status !== 200) return;
			setStandards(res.data.data);
			setStandardToMap(res.data.data[0] ?? '');
		}).catch(err => { });
	};

	const getQuestionsData = () => {
		setLoaderDisplay(true);
		QuestionService.getQuestions(standardToMap, page).then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;
			res.data.data.reportingStudio?.sort((a, b) => a.sortOrder - b.sortOrder);
			setQuestions(res.data.data.reportingStudio);
		}).catch(err => {
			setLoaderDisplay(false);
			setQuestions([]);
		});
	};

	const deleteQuestionsSet = (key) => {
		setLoaderDisplay(true);
		QuestionService.deleteFullSetQuestions(key).then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;
			toast.success('Item deleted successfully', {
				position: toast.POSITION.TOP_RIGHT,
			});
			setSelectedForDelete(null);
			getQuestionsData();
		}).catch(err => {
			setLoaderDisplay(false);
			setQuestions([]);
		});
	};

	const handleAddQuestionClose = () => {
		setShowAddQuestion(false);
	}

	const handleNewButtonClick = () => {
		setNewData({
			...newData,
			page: page // Set the current page value
		});
		setShowAddQuestion(true);
	}

	const handleKeyValueChange = (index, field, value) => {
		setNewData((prev) => {
			const updatedKeyValues = [...prev.keyValues];
			updatedKeyValues[index][field] = value;
			return { ...prev, keyValues: updatedKeyValues };
		});
	};

	const handleLocalizationChange = (index, field, value) => {
		setNewData((prev) => {
			const updatedLocalization = [...prev.localization];
			updatedLocalization[index][field] = value;
			return { ...prev, localization: updatedLocalization };
		});
	};

	const addLocalizationQn = () => {
		if (newData.localization.length < 3) {
			setNewData((prev) => ({
				...prev,
				localization: [...prev.localization, { languageCode: "", text: "", type: "REPORTING_STUDIO" }]
			}));
		}
	};

	const deleteLocalizationQn = (index) => {
		setNewData((prev) => ({
			...prev,
			localization: prev.localization.filter((_, i) => i !== index)
		}));
	};

	const saveQuestion = () => {
		const isTableType = isTableTypeKey(newData.keyValueType);
		let keyValuesToSend = [];
		if (isTableType) {
			// Use keyValues state (same as TYPE_5); if empty use one default row so we never send []
			const sourceKeyValues = keyValues.length > 0 ? keyValues : [getDefaultKeyValueRow()];
			const keyValuesWithDataPeriod = sourceKeyValues.map(kv => ({
				...kv,
				dataPeriod: kv.dataPeriod || DataPeriod.NONE
			}));
			keyValuesToSend = Utils.trimPropertyValues(keyValuesWithDataPeriod, 'key');
		} else if (newData.keyValueType === 'SINGLE_VALUE') {
			keyValuesToSend = Utils.trimPropertyValues([{ key: newData.key }], 'key');
		}
		const passData = {
			...newData,
			key: Utils.trimPropertyValues(newData.key, 'key'),
			sortOrder: newData.sortOrder === 0 ? questions.length + 1 : newData.sortOrder,
			...(isTableType && { keyValues: keyValuesToSend }),
			...(newData.keyValueType === 'SINGLE_VALUE' && { keyValues: keyValuesToSend })
		};
		// return;
		setLoaderDisplay(true);
		QuestionService.saveQuestions({ type: standardToMap, reportingStudio: [passData] }, newData.id > 0).then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;
			toast.success('Question added successfully', {
				position: toast.POSITION.TOP_RIGHT,
			});
			setShowAddQuestion(false);
			reset();
			setSaveChanges(false);
			getQuestionsData();
		}).catch(err => { setLoaderDisplay(false); });
	}

	const saveQuestionPositions = () => {
		const passData = [];
		questions.forEach(element => {
			passData.push({
				key: element.key,
				page: element.page,
				sortOrder: element.sortOrder
			});
		})
		setLoaderDisplay(true);
		QuestionService.saveQuestionsPosition({ type: standardToMap, reportingStudio: passData }).then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;
			toast.success('Position updated successfully', {
				position: toast.POSITION.TOP_RIGHT,
			});
			setShowAddQuestion(false);
			reset();
			setSaveChanges(false);
			getQuestionsData();
		}).catch(err => { setLoaderDisplay(false); });
	}

	const reset = () => {
		setNewData({
			key: "",
			frameworkCode: "",
			groupId: "",
			page: page,
			keyValueType: "PAGE",
			sortOrder: 0,
			keyValues: [],
			localization: [{
				languageCode: "EN",
				text: "",
				type: "REPORTING_STUDIO",
			}]
		});
		setKeyValues([getDefaultKeyValueRow()])
	}

	const setupEdit = (item) => {
		// Convert page to string if it's a number (API returns number, form uses string)
		const pageValue = String(item.page || '');
		const patchData = {
			id: item.id,
			key: item.key,
			frameworkCode: item.frameworkCode,
			groupId: item.groupId,
			page: pageValue,
			keyValueType: item.keyValueType,
			sortOrder: item.sortOrder,
			keyValues: [],
			localization: item.localization
		}
		setNewData(patchData);
		if (item.keyValueType == "TYPE_1_TABLE" || item.keyValueType == "TYPE_2_TABLE" || item.keyValueType == "TYPE_3_TABLE" || item.keyValueType == "TYPE_5_TABLE" || item.keyValueType == "TYPE_6_TABLE" || item.keyValueType == "TYPE_7_TABLE") {
			// Ensure dataPeriod exists for each keyValue, default to NONE if not present (same as TYPE_5)
			// Handle undefined/empty item.keyValues so TYPE_6 always has at least one row to edit
			const sourceKeyValues = Array.isArray(item.keyValues) && item.keyValues.length > 0 ? item.keyValues : [getDefaultKeyValueRow()];
			const updatedKeyValues = sourceKeyValues.map((kv) => {
				// Check if dataPeriod exists and normalize it
				let normalizedDataPeriod = DataPeriod.NONE;
				if (kv.dataPeriod) {
					if (kv.dataPeriod === DataPeriod.BASE_YEAR || kv.dataPeriod === 'BASE_YEAR') {
						normalizedDataPeriod = DataPeriod.BASE_YEAR;
					} else if (kv.dataPeriod === DataPeriod.NONE || kv.dataPeriod === 'NONE') {
						normalizedDataPeriod = DataPeriod.NONE;
					}
				}
				return {
					...kv,
					dataPeriod: normalizedDataPeriod
				};
			});
			setKeyValues(updatedKeyValues);
		}
		setShowAddQuestion(true);
	}

	const moveTotalItem = (index, direction) => {
		const data = [...questions];
		const targetIndex = index + direction;
		if (targetIndex >= 0 && targetIndex < data.length) {
			[data[index], data[targetIndex]] = [data[targetIndex], data[index]];
			data.forEach((item, i) => {
				item.sortOrder = i + 1;
			});
			setQuestions(data);
		}
		setSaveChanges(true);
	};

	const handleTranslate = (targetLang, type, index, childIndex) => {
		if (type == 'main') {
			const inputText = newData?.localization[0]?.text;
			const sourceLang = newData?.localization[0]?.languageCode;
			if (inputText != '' && sourceLang != null) {
				QuestionService.translateText(inputText, sourceLang, targetLang).then(res => {
					if (res.status !== 200) return;
					const result = res.data.translations;
					const data = { ...newData };
					data.localization[index].text = result[0]?.text;
					setNewData(data);
				}).catch(err => { });
			} else {
				toast.warning('Please provide source and text', {
					position: toast.POSITION.TOP_RIGHT,
				});
			}
		} else if (type == 'table') {
			const inputText = keyValues[index]?.metadata?.localization[0]?.text;
			const sourceLang = keyValues[index]?.metadata?.localization[0]?.languageCode;
			if (inputText != '' && sourceLang != null) {
				QuestionService.translateText(inputText, sourceLang, targetLang).then(res => {
					if (res.status !== 200) return;
					const result = res.data.translations;
					const data = [...keyValues]
					data[index].metadata.localization[childIndex].text = result[0]?.text;
					setKeyValues(data);
				}).catch(err => { });
			} else {
				toast.warning('Please provide source and text', {
					position: toast.POSITION.TOP_RIGHT,
				});
			}
		}
	}


	const handleDragStart = (index) => {
		setDraggingItemIndex(index);
	};

	const handleDragOver = (event) => {
		event.preventDefault();
	};

	const handleDrop = (index) => {
		const updatedData = [...questions];
		const [draggedItem] = updatedData.splice(draggingItemIndex, 1);
		updatedData.splice(index, 0, draggedItem);

		const reorderedData = updatedData.map((item, idx) => ({
			...item,
			sortOrder: idx + 1,
		}));
		setSaveChanges(true);
		setQuestions(reorderedData);
		setDraggingItemIndex(null);
	};

	const confirmDelete = (val) => {
		setShowDeleteConfirmation(false);
		if (val) {
			deleteQuestionsSet(selectedForDelete);
		}
	};

	const getAvailableOptions = (code) => {
		const selectedValues = newData.localization.map(row => row.languageCode).filter(Boolean);
		return languageOptions.filter(
			option =>
				!selectedValues.includes(option.value) ||
				newData.localization.find(row => row.languageCode === code)?.languageCode === option.value
		);
	};

	const getAvailableOptionsTable = (code, index) => {
		const selectedValues = keyValues[index].metadata.localization.map(row => row.languageCode).filter(Boolean);
		return languageOptions.filter(
			option =>
				!selectedValues.includes(option.value) ||
				keyValues[index].metadata.localization.find(row => row.languageCode === code)?.languageCode === option.value
		);
	};

	const getPagesForStandard = (standard) => {
		if (standard === 'VSME') {
			return VSME_PAGES;
		} else if (standard === 'GHG') {
			return GHG_PAGES;
		}
		return Pages; // Assuming 'Pages' holds the default list
	};

	const canDisplayFrameworkAndGroup = () => {
		return newData.keyValueType == 'SINGLE_VALUE' 
		|| newData.keyValueType == 'TYPE_1_TABLE' 
		|| newData.keyValueType == 'TYPE_2_TABLE' 
		|| newData.keyValueType == 'TYPE_3_TABLE'
		|| newData.keyValueType == 'TYPE_5_TABLE'
		|| newData.keyValueType == 'TYPE_7_TABLE'
		|| newData.keyValueType == 'TYPE_6_TABLE';
	};

	const canDisplayTableValues = () => {
		return newData.keyValueType == 'TYPE_1_TABLE' 
		|| newData.keyValueType == 'TYPE_2_TABLE' 
		|| newData.keyValueType == 'TYPE_3_TABLE'
		|| newData.keyValueType == 'TYPE_5_TABLE'
		|| newData.keyValueType == 'TYPE_7_TABLE'
		|| newData.keyValueType == 'TYPE_6_TABLE';
	};

	return (
		<div className="CodeMapping">
			<div className="List_header">
				<Breadcrumb className="h5">
					<Breadcrumb.Item href="#">{t("Questions.label")}</Breadcrumb.Item>
					<Breadcrumb.Item active>{t('ReportingStudio.label')}</Breadcrumb.Item>
				</Breadcrumb>

			</div>
			<div className="row m-0">
				<div className="col-lg-12 p-2 select_mapStandard">
					<div className="select_mapStandard_inner">
						{t('StandardForQuestions.label')}
						<Form.Select size="sm" className="w-auto ms-2"
							onChange={(e) => {
								const selectedStandard = e.target.value;
								setStandardToMap(selectedStandard);
								
								// Set default page based on standard
								let defaultPage = '1';
								
								// If switching to GHG and current page is greater than 5, reset to 1
								if (selectedStandard === 'GHG' && parseInt(page) > 5) {
									defaultPage = '1';
								} else if (selectedStandard !== 'GHG' && selectedStandard !== 'VSME') {
									// For ESRS and other standards, keep current page if valid, otherwise default to 1
									defaultPage = page;
								}
								
								setPage(defaultPage);
								setNewData(prev => ({ ...prev, page: defaultPage }));
							}}
							selected={standardToMap}>
							{QUESTION_STANDARDS.map((standard, index) => (
								<option key={`std-${index}`} value={standard.value}>{standard.label}</option>
							))}
						</Form.Select>
						<Form.Select size="sm" className="w-auto ms-2"
							onChange={(e) => { setPage(e.target.value); setNewData({ ...newData, page: e.target.value }) }}
							selected={page}>
							{
								standardToMap === 'VSME' ?
									VSME_PAGES.map((page, index) => (
										<option key={page.key} value={page.key}>{page.text}</option>
									)) :
								standardToMap === 'GHG' ?
									GHG_PAGES.map((page, index) => (
										<option key={page.key} value={page.key}>{page.text}</option>
									)) :
								standardToMap === 'UAE_FDL' ?
									UAE_FDL_PAGES.map((page, index) => (
										<option key={page.key} value={page.key}>{page.text}</option>
									)) :
									Pages?.map((page, index) => (
										<option key={page.key} value={page.key}>{page.text}</option>
									))
							}
						</Form.Select>
					</div>
					<div className="py-2 px-0">
						<Button variant="primary" size="sm" onClick={() => saveQuestionPositions()} disabled={!saveChanges}>Update Position</Button>&nbsp;&nbsp;
						<Button variant="secondary" size="sm" onClick={handleNewButtonClick}><BiPlus />{t('New.label')}</Button>
					</div>
				</div>
				<div className="Questions_listbox">
					<Table responsive bordered hover className='mb-0'>
						<thead>
							<tr>
								<th className="text-center">Order</th>
								<th>Type</th>
								<th>Text</th>
								<th className="text-center">Identifier</th>
								<th className="text-center">Framework Code</th>
								<th className="text-center">ECD Codes</th>
								<th className="text-center">Group ID</th>
								<th className="text-center">Action</th>
							</tr>
						</thead>
						<tbody>
							{questions?.length > 0 ? (
								questions?.map((item, index) => (
									<tr key={item.key}
										draggable
										onDragStart={() => handleDragStart(index)}
										onDragOver={handleDragOver}
										onDrop={() => handleDrop(index)}
									>
										<td className="text-center">{item.sortOrder}</td>
										<td>{QuestionTypeLabel[item.keyValueType]}</td>
										<td width={'50%'}>
											{
												item?.localization?.map((data, key) => (
													<>
														<label><b>{data.languageCode}</b> - </label>
														<label>{data.text}</label><br />
													</>
												))
											}
										</td>
										<td className="text-center">{item.key}</td>
										<td className="text-center">{item.frameworkCode}</td>
										<td className="text-center"></td>
										<td className="text-center">{item.groupId}</td>
										<td className="text-center">
											<span className="cursor" onClick={() => setupEdit(item)}><BiPencil /></span>&nbsp;&nbsp;
											{/* <span className='cursor' onClick={() => moveTotalItem(index, -1)}
												disabled={index === 0}>
												<BiUpArrow />
											</span>&nbsp;&nbsp;
											<span className='cursor' onClick={() => moveTotalItem(index, 1)}
												disabled={index === questions.length - 1}>
												<BiDownArrow />
											</span>&nbsp;&nbsp; */}
											<span className="cursor" onClick={() => { setShowDeleteConfirmation(true); setSelectedForDelete(item.key) }}><BiTrash /></span>
										</td>
									</tr>
								))
							) : (
								<tr>
									<td colSpan="7" className="text-center">
										<div className="Nodatafound_box">
											<h6><FcFinePrint className="me-2" /> {t("Nodatafound.label")}</h6>
										</div>
									</td>
								</tr>
							)}
						</tbody>
					</Table>
				</div>
			</div>

			<Modal show={showAddQuestion} onHide={() => { handleAddQuestionClose(); reset() }}
				size='lg'
				aria-labelledby="contained-modal-title-vcenter"
				centered
				backdrop="static"
				keyboard={false}>
				<Modal.Header closeButton>
					<Modal.Title as="h5">Add Question</Modal.Title>
				</Modal.Header>
				<Modal.Body className="AddQuestion_modalbody">
					<>
						<div className="row m-0 p-2">
							<div className="col-lg-7 p-2">
								<label className="mb-1">
									{t("Type.label")} <span className="text-danger">*</span>
								</label>
								<select className="form-select" value={newData.keyValueType}
									onChange={(e) => {
										const newType = e.target.value;
										setNewData({ ...newData, keyValueType: newType });
										// When switching to a table type (e.g. TYPE_5/TYPE_6), ensure keyValues has at least one row so form and save work like TYPE_5
										if (isTableTypeKey(newType)) {
											setKeyValues((prev) => (prev.length === 0 ? [getDefaultKeyValueRow()] : prev));
										}
									}}>
									{
										QuestionType?.filter((el) => {
											// Only show TYPE_6_TABLE and TYPE_7_TABLE for GHG standard
											if (el.key === 'TYPE_6_TABLE' || el.key === 'TYPE_7_TABLE') {
												return standardToMap === 'GHG';
											}
											return true;
										}).map((el) => (
											<option value={el.key} key={el.key}>{el.text}</option>
										))
									}
								</select>
							</div>
							<div className="col-lg-3 p-2">
								<label className="mb-1">
									{t("Page.label")} <span className="text-danger">*</span>
								</label>
								<select className="form-select" value={newData.page}
									onChange={(e) => { setNewData({ ...newData, page: e.target.value }) }}>
									{
										getPagesForStandard(standardToMap)?.map((page, index) => (
											<option key={page.key} value={page.key}>{page.text}</option>
										))
									}
								</select>
							</div>
							<>
								<div className="p-0">
									<div className='row m-0 align-items-end'>
										<div className='col-md-2 px-2'>
											<label className='mb-1'>Language <span className="text-danger">*</span></label>
										</div>
										<div className='col-md-8 px-2'>
											<label className='mb-1'>Text <span className="text-danger">*</span></label>
										</div>
									</div>

									{newData.localization.map((item, index) => (
										<div className='row m-0 align-items-end'>
											<div className='col-md-2 px-2 py-1'>
												<select className="form-select" value={item.languageCode}
													onChange={(e) => handleLocalizationChange(index, "languageCode", e.target.value)}>
													<option value="">Select a language</option>
													{getAvailableOptions(item.languageCode).map(option => (
														<option value={option.value} key={option.value}>
															{option.label}
														</option>
													))}
												</select>
											</div>
											<div className='col-md-8 px-2 py-1'>
												<input
													className="form-control"
													type="text"
													placeholder="Text"
													value={item.text}
													onChange={(e) => handleLocalizationChange(index, "text", e.target.value)}
												/>
											</div>
											<div className='col-md-2 px-2  py-1'>
												<ul className='stakeCat_add'>
													{
														(index > 0) && (
															<OverlayTrigger
																placement="top"
																overlay={Utils.renderTooltip(
																	'Click to Translate'
																)}>
																<li>
																	<span className='cursor' onClick={() => handleTranslate(item.languageCode, 'main', index, null)}>
																		<BiTransfer />
																	</span>
																</li>
															</OverlayTrigger>
														)
													}
													{
														(newData.localization.length > 1) && (
															<OverlayTrigger
																placement="top"
																overlay={Utils.renderTooltip(
																	'Delete'
																)}>
																<li>
																	<span className='cursor' onClick={() => deleteLocalizationQn(index)}>
																		<BiTrash />
																	</span>
																</li>
															</OverlayTrigger>
														)}
													{
														(index === newData.localization.length - 1) && (
															<OverlayTrigger
																placement="top"
																overlay={Utils.renderTooltip(
																	'Add new'
																)}>
																<li>
																	<Button variant='GroupStake' className='btn-outline-primary border-0' onClick={() => addLocalizationQn()}> <BiPlusCircle /></Button>
																</li>
															</OverlayTrigger>
														)}
												</ul>
											</div>
										</div>
									))}
								</div>
							</>
							<div className="col-lg-4 p-2">
								<label className="mb-1">
									{t("Identifier.label")}<span className="text-danger">*</span>
								</label>
								<input type="text" className="form-control" value={newData.key}
									onChange={(e) => setNewData({ ...newData, key: e.target.value })} />
							</div>
							{
								canDisplayFrameworkAndGroup() &&
								<>
									<div className="col-lg-4 p-2">
										<label className="mb-1">
											{t("FrameworkText.label")} <span className="text-danger">*</span>
										</label>
										<input type="text" className="form-control" value={newData.frameworkCode}
											onChange={(e) => setNewData({ ...newData, frameworkCode: e.target.value })} />
									</div>
									<div className="col-lg-4 p-2">
										<label className="mb-1">
											{t("GroupId.label")}
										</label>
										<input type="text" className="form-control" value={newData.groupId}
											onChange={(e) => setNewData({ ...newData, groupId: e.target.value })} />
									</div>
								</>
							}
							{
								canDisplayTableValues() &&
								<div className="col-lg-12 p-2">
									<h6>Table Values</h6>
									{keyValues.map((keyValue, keyIndex) => (
										<div >
											<div key={keyIndex} className='Table_CardValues'>
												<div className='row m-0'>

													<div className='col-md-4 p-2'>
														<label className="mb-1">
															Cell Type<span className="text-danger">*</span>
														</label>
														<select className="form-select" value={keyValue.metadata.columnType}
															onChange={(e) => updatePositionValue(keyIndex, "columnType", e.target.value)}>
															{
																newData.keyValueType != 'TYPE_1_TABLE' && newData.keyValueType != 'TYPE_5_TABLE' && newData.keyValueType != 'TYPE_6_TABLE' && newData.keyValueType != 'TYPE_7_TABLE' &&
																<>
																	<option value={'STATIC_TEXT'} key={'STATIC_TEXT'}>Static Text</option>
																	<option value={'BLANK'} key={'BLANK'}>Blank</option>
																</>
															}
															<option value={'CODE_MAPPING'} key={'CODE_MAPPING'}>Code Mapping</option>
															<option value={'HEADER'} key={'HEADER'}>Header</option>
														</select>
													</div>
													<div className='col-md-4 p-2'>
														<label className="mb-1">
															Cell Number <span className="text-danger">*</span>
														</label>
														<input type="text" className="form-control" value={keyValue.metadata.columnNumber}
															onChange={(e) => updatePositionValue(keyIndex, "columnNumber", e.target.value)} />
													</div>
													<div className='col-md-4 p-2'>
														<label className="mb-1">
															{t("Identifier.label")} <span className="text-danger">*</span>
														</label>
														<input
															className="form-control"
															type="text"
															placeholder="Key"
															value={keyValue.key}
															onChange={(e) => updateKeyValue(keyIndex, "key", e.target.value)}
														/>
													</div>
													{
														(standardToMap === 'GHG' && newData.keyValueType === 'TYPE_2_TABLE' && keyValue.metadata.columnType === 'CODE_MAPPING') &&
														<div className='col-md-12 p-2'>
															<Form.Check
																type="checkbox"
																id={`dataPeriod-checkbox-${keyIndex}`}
																label="From Base Year"
																checked={(() => {
																	const currentDataPeriod = keyValue.dataPeriod || DataPeriod.NONE;
																	return currentDataPeriod === DataPeriod.BASE_YEAR || currentDataPeriod === 'BASE_YEAR';
																})()}
																onChange={(e) => updateDataPeriod(keyIndex, e.target.checked)}
															/>
														</div>
													}
													{
														(keyValue.metadata.columnType != 'CODE_MAPPING' && keyValue.metadata.columnType != 'BLANK') &&
														<div className="p-0">
															<div className='row m-0'>
																<div className='col-md-2 px-2'>
																	<label className='mb-1'>Language <span className="text-danger">*</span></label>
																</div>
																<div className='col-md-8 px-2'>
																	<label className='mb-1'>Header Text <span className="text-danger">*</span></label>
																</div>
															</div>
															<>
																{keyValue.metadata.localization.map((localization, locIndex) => (
																	<div className='row m-0 align-items-end'>
																		<div className='col-md-2 px-2 py-1'>
																			<select className="form-select" value={localization.languageCode}
																				onChange={(e) =>
																					updateLocalization(keyIndex, locIndex, "languageCode", e.target.value)
																				}>
																				<option value="">Select a language</option>
																				{getAvailableOptionsTable(localization.languageCode, keyIndex).map(option => (
																					<option value={option.value} key={option.value}>
																						{option.label}
																					</option>
																				))}
																			</select>
																		</div>

																		<div className='col-md-8 px-2 py-1'>
																			<input
																				className="form-control"
																				type="text"
																				placeholder="Text"
																				value={localization.text}
																				onChange={(e) =>
																					updateLocalization(keyIndex, locIndex, "text", e.target.value)
																				}
																			/>
																		</div>

																		<div className='col-md-2 px-2 py-1'>
																			<ul className='stakeCat_add'>
																				{
																					(locIndex > 0) && (
																						<li>
																							<span className='cursor' onClick={() => handleTranslate(localization.languageCode, 'table', keyIndex, locIndex)}>
																								<BiTransfer />
																							</span>
																						</li>
																					)
																				}
																				{
																					(keyValue.metadata.localization.length > 1) && (
																						<OverlayTrigger
																							placement="top"
																							overlay={Utils.renderTooltip(
																								'Delete'
																							)}>
																						<li>
																								<span className='cursor' onClick={() => deleteLocalization(keyIndex, locIndex)}>
																									<BiTrash />
																								</span>
																							</li>
																						</OverlayTrigger>
																					)}
																				{
																					(locIndex === keyValue.metadata.localization.length - 1) && (
																						<OverlayTrigger
																							placement="top"
																							overlay={Utils.renderTooltip(
																								'Add new'
																							)}>
																							<li>
																								<Button variant='GroupStake' className='btn-outline-primary border-0' onClick={() => addLocalization(keyIndex)}> <BiPlusCircle /></Button>
																							</li>
																						</OverlayTrigger>
																					)}
																				</ul>
																		</div>
																	</div>
																))}
															</>
														</div>
													}

													<div className='col-md-12 px-1 pt-2'>
														<ul className="Table_CardValues_btn">
															{
																(keyValues.length > 1) && (
																	<OverlayTrigger
																		placement="top"
																		overlay={Utils.renderTooltip(
																			'Delete'
																		)}>
																		<li onClick={() => deleteKeyValue(keyIndex)}>
																			<BiTrash />
																		</li>
																	</OverlayTrigger>
																)}
															<OverlayTrigger
																placement="top"
																overlay={Utils.renderTooltip(
																	'Move Up'
																)}>
																<li onClick={() => moveKeyValue(keyIndex, "up")}>
																	<BiUpArrow />
																</li>
															</OverlayTrigger>

															<OverlayTrigger
																placement="top"
																overlay={Utils.renderTooltip(
																	'Move Down'
																)}>
																<li onClick={() => moveKeyValue(keyIndex, "down")}>
																	<BiDownArrow />
																</li>
															</OverlayTrigger>
														</ul>
													</div>
												</div>
											</div>
											<div>
												{
													(keyIndex === keyValues.length - 1) && (
														<OverlayTrigger
															placement="top"
															overlay={Utils.renderTooltip(
																'Add new cell'
															)}>
															<Button variant='GroupStake' className='btn-outline-primary ' onClick={() => addKeyValue()}> <BiPlusCircle className="me-2" /> Cell</Button>
														</OverlayTrigger>
													)}
											</div>
										</div>
									))}
								</div>
							}
						</div>
					</>
				</Modal.Body>
				<Modal.Footer className="border-0 pt-0">
					<Button variant="secondary" className='px-4 me-2' onClick={() => { handleAddQuestionClose(); reset() }}>
						Close
					</Button>
					<Button variant="primary" className='px-4' onClick={() => saveQuestion()}>
						Save
					</Button>
				</Modal.Footer>
			</Modal>

			{
				showDeleteConfirmation &&
				<ConfirmationModal
					modalShow={true}
					messageText={t("ConfirmDelete.label")}
					callbackModal={confirmDelete}
					confirmBtn={t("Confirm.label")}
					CancelBtn={t("Cancel.label")}
				/>
			}
		</div>
	);

};
export default withTranslation()(Questions);
